import {
  displayCurrency,
  displayDistributions,
  sumAllDistributions,
  sumAllSurcharges,
} from 'app/scripts/PricingHelper';
import DataMap from 'assets/components/dataMap/DataMap';
import FormatValue from 'assets/components/formatValue/FormatValue';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import ObjectInfo from 'assets/templates/objectInfo/ObjectInfo';
import { defaultDateFormat, localizeDate } from 'assets/utils/data/Date';
import { isEmpty } from 'lodash';
import { useSalesContractProvider } from '../../Sales.Contract.Hooks';

export default function ContractInfoCancellation() {
  const { lang } = getLocales();
  const { isDealerOrDistributor, isDealer } = useAuthStore();
  const { subfeeNameMap, contractCancellation, contractCancellationListApi } = useSalesContractProvider();

  if (!contractCancellation) return null;

  const totalSurcharges = sumAllSurcharges(contractCancellation?.refundSubfees);
  const totalPricingDistributions = sumAllDistributions(contractCancellation?.refundPricingDistributions);
  const totalDistributorDistributions = sumAllDistributions(contractCancellation?.refundDistributorDistributions);

  const enhancementRefundPrices = contractCancellation?.refundEnhancements?.map((it) => ({
    totalPricingDistributions: sumAllDistributions(it.pricingDistributions),
    totalDistributorDistributions: sumAllDistributions(it.distributorDistributions),
  }));

  const totalEnhancements = enhancementRefundPrices?.reduce((acc, it) => acc + it.totalPricingDistributions, 0) ?? 0;

  const totalEnhancementsDistributor =
    enhancementRefundPrices?.reduce((acc, it) => acc + it.totalDistributorDistributions, 0) ?? 0;

  const grandTotal =
    totalSurcharges +
    totalPricingDistributions +
    totalDistributorDistributions +
    totalEnhancements +
    totalEnhancementsDistributor;

  return (
    <>
      <h1>{lang.cancellationHistory}</h1>
      <table className={ThemeTableSimple}>
        <thead>
          <tr>
            <th>{lang.cancellationDate}</th>
            <th>{lang.cancellationReason}</th>
            <th>{lang.cancellationReasonDescription}</th>
            <th>{lang.odometerReading}</th>
          </tr>
        </thead>
        <tbody>
          <DataMap
            data={contractCancellationListApi?.payload?.data}
            render={({ data: cancellation }) => (
              <tr key={cancellation.id}>
                <td>{localizeDate(cancellation.cancellationDateLocal, defaultDateFormat)}</td>
                <td>{cancellation.reason}</td>
                <td>{cancellation.reasonDescription}</td>
                <td>{cancellation.odometerReading && <FormatValue value={`${cancellation.odometerReading}km`} />}</td>
              </tr>
            )}
          />
        </tbody>
      </table>

      {contractCancellation?.refundDateAtLocal && (
        <>
          <h1>{lang.cancellationInfo}</h1>
          <div>
            {lang.refundDate}: <b>{localizeDate(contractCancellation?.refundDateAtLocal, defaultDateFormat)}</b>
          </div>
        </>
      )}

      {!isDealerOrDistributor && !isEmpty(contractCancellation?.refundPricingDistributions) && (
        <>
          <ObjectInfo
            object={displayDistributions(contractCancellation?.refundPricingDistributions)}
            objectName={lang.pricingDistributions}
            disableTranslation
          />
          <div style={{ marginTop: '0.5rem' }}>
            <hr />
            {lang.total} {lang.pricingDistributions.toLowerCase()}:{' '}
            <b>
              <FormatValue value={displayCurrency(totalPricingDistributions)} />
            </b>
          </div>
        </>
      )}

      {!isDealer && !isEmpty(contractCancellation?.refundDistributorDistributions) && (
        <>
          <ObjectInfo
            object={displayDistributions(contractCancellation?.refundDistributorDistributions)}
            objectName={lang.distributorDistributions}
            disableTranslation
          />
          <div style={{ marginTop: '0.5rem' }}>
            <hr />
            {lang.total} {lang.distributorDistributions.toLowerCase()}:{' '}
            <b>
              <FormatValue value={displayCurrency(totalDistributorDistributions)} />
            </b>
          </div>
        </>
      )}

      {!isEmpty(subfeeNameMap) && !isEmpty(contractCancellation?.refundSubfees) && (
        <>
          <h3>{lang.surcharges}</h3>
          <DataMap
            data={contractCancellation?.refundSubfees}
            render={({ data: subfee }) => (
              <div key={subfee?.subfeeId}>
                {subfeeNameMap[subfee?.subfeeId?.toString()] || lang.unknown}:{' '}
                <b>
                  {subfee?.quantity} x <FormatValue value={displayCurrency(subfee?.amount || 0, subfee?.currency)} />
                </b>
              </div>
            )}
          />
          <hr />
          <div style={{ marginTop: '0.5rem' }}>
            {lang.total} {lang.surcharges.toLowerCase()}:{' '}
            <b>
              <FormatValue value={displayCurrency(totalSurcharges)} />
            </b>
          </div>
        </>
      )}

      {!isEmpty(contractCancellation?.refundEnhancements) && (
        <>
          <h1>{lang.enhancementsDistributions}</h1>

          {contractCancellation?.refundEnhancements.map((enh, idx) => {
            return (
              <div key={idx}>
                <h2 style={{ marginTop: '2rem', marginBottom: '0.5rem' }}>{enh.name}</h2>

                {!isEmpty(enh.pricingDistributions) && (
                  <ObjectInfo
                    object={displayDistributions(enh.pricingDistributions)}
                    objectName={lang.pricingDistributions}
                    disableTranslation
                  />
                )}
                {!isEmpty(enh.distributorDistributions) && (
                  <ObjectInfo
                    object={displayDistributions(enh.distributorDistributions)}
                    objectName={lang.distributorDistributions}
                    disableTranslation
                  />
                )}
              </div>
            );
          })}

          <div style={{ marginTop: '0.5rem' }}>
            <hr />
            {lang.total} {lang.pricingDistributions.toLowerCase()}:{' '}
            <b>
              <FormatValue value={displayCurrency(totalEnhancements)} />
            </b>
          </div>

          <div style={{ marginTop: '0.5rem' }}>
            <hr />
            {lang.total} {lang.distributorDistributions.toLowerCase()}:{' '}
            <b>
              <FormatValue value={displayCurrency(totalEnhancementsDistributor)} />
            </b>
          </div>
        </>
      )}

      {!!grandTotal && (
        <div>
          <h2>{lang.total}</h2>
          <hr />
          <b>
            <FormatValue value={displayCurrency(grandTotal)} />
          </b>
        </div>
      )}
    </>
  );
}
